import { Quaternion, Vector3 } from 'three'
import { aimQuaternion, GRIP_PRESETS, gripById, gripPosition, type GripPreset } from './gripPresets'
import type { InputSource, PilotInput } from './types'

const MAX_AIM = (65 * Math.PI) / 180
const DEADZONE = 0.12
const LEAN_DISTANCE = 0.22
const LEAN_RATE = 3
const KEY_THROTTLE_RATE = 4
/** Metres per second while an offset key is held. */
const OFFSET_RATE = 0.5

/** Live nudges applied on top of the grip preset, in tracking space. */
export interface HandOffset {
  /** + raises both hands (above the CoM → hanging pendulum). */
  height: number
  /** + moves both hands outboard (longer lever, more torque per newton). */
  spread: number
  /** + reaches both hands forward (constant pitch-back torque under up-thrust). */
  forward: number
}

const OFFSET_LIMITS: Record<keyof HandOffset, [number, number]> = {
  height: [-0.55, 0.7],
  spread: [-0.28, 0.6],
  forward: [-0.3, 0.6],
}

/**
 * Desktop flight input — layer 2 of the desktop sim (DESIGN.md §4).
 *
 * Deliberately **not** a VR controller emulator. Free-posing two 6-DOF hands
 * with a mouse is a known failure mode that tests nothing real, so hand
 * positions are a grip preset plus three named offsets — height, spread and
 * reach — rather than an arbitrary pose. Those three are the axes that change
 * the physics; the rest of a hand's six degrees of freedom do not.
 *
 * Falls back to the keyboard when no pad is connected.
 */
export class GamepadSource implements InputSource {
  readonly name = 'gamepad'

  /** Body orientation, written by the app each frame — needed for world aim. */
  readonly bodyQuaternion = new Quaternion()

  /**
   * Hold the nozzles pointed down in *world* space rather than fixed to the
   * body. A test aid, not the game.
   *
   * With body-fixed aim the thrust vector rotates with the body, so attitude
   * never feeds back into torque and grip height is physically invisible — no
   * grip can diverge or restore. In the headset the player breaks that loop by
   * physically re-aiming at the ground, which is the core skill. On a keyboard
   * there is nothing to break it with, so this stands in for a perfect pilot
   * and makes the high/low grip mechanic observable on the desktop.
   */
  holdWorldDown = false

  readonly offset: HandOffset = { height: 0, spread: 0, forward: 0 }

  private grip: GripPreset = gripById('mid')
  private lean = new Vector3()
  private leanTarget = new Vector3()
  private readonly keys = new Set<string>()
  private keyThrottle: [number, number] = [0, 0]
  private padIndex: number | null = null

  constructor(private standingHeight: number) {
    window.addEventListener('keydown', this.onKeyDown)
    window.addEventListener('keyup', this.onKeyUp)
  }

  setStandingHeight(h: number): void {
    this.standingHeight = h
  }

  get gripLabel(): string {
    return this.grip.label
  }

  /** Short description of what is currently driving the sim, for the HUD. */
  get status(): string {
    const pad = this.pad()
    return pad ? `pad: ${pad.id.slice(0, 28)}` : 'keyboard (no pad)'
  }

  private pad(): Gamepad | null {
    const pads = navigator.getGamepads?.() ?? []
    if (this.padIndex !== null && pads[this.padIndex]) return pads[this.padIndex]
    for (let i = 0; i < pads.length; i++) {
      if (pads[i]?.connected) {
        this.padIndex = i
        return pads[i]
      }
    }
    return null
  }

  sample(out: PilotInput, dt: number): void {
    const pad = this.pad()

    let throttleL = 0
    let throttleR = 0
    const aim: [[number, number], [number, number]] = [
      [0, 0],
      [0, 0],
    ]

    if (pad) {
      throttleL = pad.buttons[6]?.value ?? 0
      throttleR = pad.buttons[7]?.value ?? 0
      aim[0] = [-dz(pad.axes[1] ?? 0) * MAX_AIM, dz(pad.axes[0] ?? 0) * MAX_AIM]
      aim[1] = [-dz(pad.axes[3] ?? 0) * MAX_AIM, dz(pad.axes[2] ?? 0) * MAX_AIM]

      if (pad.buttons[12]?.pressed) this.setGrip('high')
      if (pad.buttons[13]?.pressed) this.setGrip('low')
      if (pad.buttons[14]?.pressed) this.setGrip('narrow')
      if (pad.buttons[15]?.pressed) this.setGrip('wide')
      if (pad.buttons[0]?.pressed) this.setGrip('mid')
      if (pad.buttons[3]?.pressed) this.setGrip('asym')

      // Shoulders shift weight — the third control input (§2).
      const leanX = (pad.buttons[5]?.pressed ? 1 : 0) - (pad.buttons[4]?.pressed ? 1 : 0)
      this.leanTarget.set(leanX * LEAN_DISTANCE, 0, 0)

      // Keyboard offset/aim keys stay live alongside the pad, so hand geometry
      // can be dialled in without putting the pad down.
      this.sampleKeyboardAim(aim, true)
    } else {
      this.sampleKeyboard(dt, aim)
      throttleL = this.keyThrottle[0]
      throttleR = this.keyThrottle[1]
    }

    this.updateOffsets(dt)

    this.lean.lerp(this.leanTarget, 1 - Math.exp(-LEAN_RATE * dt))

    out.head.position.set(this.lean.x, this.standingHeight, this.lean.z)
    out.head.quaternion.identity()
    out.head.tracked = true

    if (this.holdWorldDown) _invBody.copy(this.bodyQuaternion).invert()

    for (let i = 0; i < 2; i++) {
      const hand = out.hands[i]
      gripPosition(i === 0 ? this.grip.left : this.grip.right, this.standingHeight, _slot)
      const outboard = i === 0 ? -1 : 1
      _slot.x += outboard * this.offset.spread
      _slot.y += this.offset.height
      _slot.z -= this.offset.forward
      hand.position.copy(_slot).add(this.lean)

      aimQuaternion(aim[i][0], aim[i][1], hand.quaternion)
      // Undo the body rotation so the commanded aim lands in world space.
      if (this.holdWorldDown) hand.quaternion.premultiply(_invBody)

      hand.throttle = i === 0 ? throttleL : throttleR
      hand.tracked = true
    }
  }

  private sampleKeyboard(dt: number, aim: [[number, number], [number, number]]): void {
    const k = this.keys
    const rate = KEY_THROTTLE_RATE * dt
    const wantBoth = k.has('KeyW')
    this.keyThrottle[0] = approach(this.keyThrottle[0], k.has('KeyA') || wantBoth ? 1 : 0, rate)
    this.keyThrottle[1] = approach(this.keyThrottle[1], k.has('KeyD') || wantBoth ? 1 : 0, rate)

    const leanX = (k.has('KeyX') ? 1 : 0) - (k.has('KeyZ') ? 1 : 0)
    this.leanTarget.set(leanX * LEAN_DISTANCE, 0, 0)

    this.sampleKeyboardAim(aim, false)
  }

  /**
   * Arrows aim both nozzles; the T/F/G/H and I/J/K/L clusters aim one rocket
   * each, which is where asymmetric torque comes from.
   */
  private sampleKeyboardAim(
    aim: [[number, number], [number, number]],
    additive: boolean,
  ): void {
    const k = this.keys
    const axis = (pos: string, neg: string): number => (k.has(pos) ? 1 : 0) - (k.has(neg) ? 1 : 0)

    const bothPitch = axis('ArrowUp', 'ArrowDown')
    const bothRoll = axis('ArrowRight', 'ArrowLeft')
    const diff = axis('BracketRight', 'BracketLeft')

    const leftPitch = axis('KeyT', 'KeyG')
    const leftRoll = axis('KeyH', 'KeyF')
    const rightPitch = axis('KeyI', 'KeyK')
    const rightRoll = axis('KeyL', 'KeyJ')

    const l: [number, number] = [
      (bothPitch + leftPitch) * MAX_AIM,
      (bothRoll - diff + leftRoll) * MAX_AIM,
    ]
    const r: [number, number] = [
      (bothPitch + rightPitch) * MAX_AIM,
      (bothRoll + diff + rightRoll) * MAX_AIM,
    ]

    aim[0][0] = clampAim(additive ? aim[0][0] + l[0] : l[0])
    aim[0][1] = clampAim(additive ? aim[0][1] + l[1] : l[1])
    aim[1][0] = clampAim(additive ? aim[1][0] + r[0] : r[0])
    aim[1][1] = clampAim(additive ? aim[1][1] + r[1] : r[1])
  }

  /** Shift + arrows moves the hands; plain arrows aims them. */
  private updateOffsets(dt: number): void {
    const k = this.keys
    if (!k.has('ShiftLeft') && !k.has('ShiftRight')) return
    const step = OFFSET_RATE * dt
    const axis = (pos: string, neg: string): number => (k.has(pos) ? 1 : 0) - (k.has(neg) ? 1 : 0)

    this.nudge('height', axis('ArrowUp', 'ArrowDown') * step)
    this.nudge('spread', axis('ArrowRight', 'ArrowLeft') * step)
    this.nudge('forward', axis('BracketRight', 'BracketLeft') * step)
  }

  private nudge(key: keyof HandOffset, delta: number): void {
    if (delta === 0) return
    const [lo, hi] = OFFSET_LIMITS[key]
    this.offset[key] = Math.max(lo, Math.min(hi, this.offset[key] + delta))
  }

  resetOffsets(): void {
    this.offset.height = 0
    this.offset.spread = 0
    this.offset.forward = 0
  }

  private setGrip(id: string): void {
    if (this.grip.id === id) return
    this.grip = gripById(id)
    this.resetOffsets()
  }

  private readonly onKeyDown = (e: KeyboardEvent): void => {
    const digit = Number(e.code.replace('Digit', ''))
    if (e.code.startsWith('Digit') && digit >= 1 && digit <= GRIP_PRESETS.length) {
      this.setGrip(GRIP_PRESETS[digit - 1].id)
    }
    if (e.code === 'KeyB') this.holdWorldDown = !this.holdWorldDown
    if (e.code === 'KeyN') this.resetOffsets()
    // Shift + arrows is a hand-position nudge, not page scrolling.
    if (e.shiftKey && e.code.startsWith('Arrow')) e.preventDefault()
    this.keys.add(e.code)
  }

  private readonly onKeyUp = (e: KeyboardEvent): void => {
    this.keys.delete(e.code)
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown)
    window.removeEventListener('keyup', this.onKeyUp)
  }
}

function dz(v: number): number {
  if (Math.abs(v) < DEADZONE) return 0
  return (v - Math.sign(v) * DEADZONE) / (1 - DEADZONE)
}

function clampAim(v: number): number {
  return Math.max(-MAX_AIM, Math.min(MAX_AIM, v))
}

function approach(current: number, target: number, rate: number): number {
  if (current < target) return Math.min(target, current + rate)
  return Math.max(target, current - rate)
}

const _slot = new Vector3()
const _invBody = new Quaternion()
